import { WORLD_TILE_PIXELS } from "odyssey-shared";
import { keyName } from "../../../ui/input/router.ts";
import type { KeyTable } from "../../controls/page.ts";
import { heldOwned } from "../../controls/press.ts";

/**
 * Which ground items show their names (P2.8-T76, the author's choice of 2026-10-10, "Nearby + hover"): an item within
 * `GROUND_LABEL_TILES` of the local character's feet, the item under the pointer, and every item while Show item
 * names' key is held (Z unless the player rebinds it in Controls; the author, 2026-10-10: "Z, rebindable"). All of it
 * is pure but the key's hold, which lives on the target the surface reads.
 */

/**
 * How near the local character's feet a ground item shows its name, in tiles, by straight distance (the author,
 * 2026-10-10: "Nearby increase to 6 tiles.").
 */
export const GROUND_LABEL_TILES = 6;

/**
 * The reach's square in world pixels, made once.
 */
const REACH_SQUARED = (GROUND_LABEL_TILES * WORLD_TILE_PIXELS) ** 2;

/**
 * Whether a ground item lies within `GROUND_LABEL_TILES` of the local character's feet.
 *
 * @param dx The item's point across from the feet, in world pixels.
 * @param dy The same down.
 * @returns True within the reach, its edge included.
 */
export function inLabelReach(dx: number, dy: number): boolean {
  return dx * dx + dy * dy <= REACH_SQUARED;
}

/**
 * The tile a feet coordinate stands in, on one axis, which the surface watches to look again at which names show.
 *
 * @param px The coordinate in world pixels.
 * @returns The tile's index.
 */
export function labelTile(px: number): number {
  return Math.floor(px / WORLD_TILE_PIXELS);
}

/**
 * The ground item under a world point: of the items whose drawn square, `half` either side of the item's point, holds
 * the point, the one whose point is nearest, the first in order on a tie (`groundUnder`'s rule, read off the drawn
 * items).
 *
 * @param readings The items drawn, with their handles and points in world pixels.
 * @param x The point's x in world pixels.
 * @param y The point's y.
 * @param half Half the drawn square's side in world pixels.
 * @returns The item's handle, or -1 for none.
 */
export function labelUnder(readings: readonly { readonly handle: number; readonly x: number; readonly y: number }[], x: number, y: number, half: number): number {
  let best = -1;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (let index = 0; index < readings.length; index += 1) {
    const reading = readings[index] as { readonly handle: number; readonly x: number; readonly y: number };
    const dx = reading.x - x;
    const dy = reading.y - y;
    if (Math.abs(dx) > half || Math.abs(dy) > half) {
      continue;
    }
    const distance = dx * dx + dy * dy;
    if (distance < bestDistance) {
      best = reading.handle;
      bestDistance = distance;
    }
  }
  return best;
}

/**
 * The hold of Show item names' key (`itemNames` in the key table, `KeyZ` by default; Alt was asked for and clashes,
 * since Phaser prevents no key pressed with a modifier, so with Alt held a step left is the browser's Back), written
 * to a target the surface reads each frame. A press holds only when the key table names it Show item names
 * (`actionOfPress`: the action on the press's physical key, or on the key it is an alias of, so on AZERTY the key in
 * W's place, which types z, is Move up and never holds), and only while the game owns the keyboard under the focus
 * rule (`heldOwned`), so typing the letter in chat shows nothing and a focused button keeps its Space and Enter. The
 * hold is remembered by the press's physical key (its character when the event names none) and ends on that key's
 * release whoever owns the keyboard then, even after a rebinding, or as the window loses focus, since the release then
 * goes elsewhere.
 *
 * @example
 * const key = new LabelKey(gameReport.groundLabels, pageKeys);
 * input.watchKeys((event, down, owned) => key.key(event.key, event.code, down, owned));
 */
export class LabelKey {
  private holding: string | null = null;

  /**
   * @param target Where the hold is written.
   * @param target.held Whether the key is held now.
   * @param keys The key table, which names a press's action.
   */
  public constructor(
    private readonly target: { held: boolean },
    private readonly keys: Pick<KeyTable, "actionOf">,
  ) {}

  /**
   * Takes a key going down or up.
   *
   * @param key The `KeyboardEvent.key` value.
   * @param code The `KeyboardEvent.code` value, the physical key, when the event says.
   * @param down Whether it went down.
   * @param owned Whether the game owned the keyboard then.
   * @param target What had focus: a button keeps its own Space and Enter (`heldOwned`).
   */
  public key(key: string, code: string | undefined, down: boolean, owned: boolean, target: unknown = null): void {
    const which = code === undefined || code === "" ? keyName(key) : code;
    if (!down) {
      if (which === this.holding) {
        this.lost();
      }
      return;
    }
    if (heldOwned({ key, code, target }, owned) && this.keys.actionOf({ key, code }) === "itemNames") {
      this.holding = which;
      this.target.held = true;
    }
  }

  /**
   * Lets the hold go, as the window loses focus.
   */
  public lost(): void {
    this.holding = null;
    this.target.held = false;
  }
}
